import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { LoginRequest, LoginResponse, AuthUser } from '../types/api';
import { apiService } from '../services/api';
import { useToast } from '../hooks/useToast';
import { fastchipClusterSettingsService } from '../services/fastchipClusterSettings/FastchipClusterSettingsService';
import { setFastchipClusterSettings } from '../utils/fastchipClusterRules';

/** Busca a config de clusters Fastchip uma vez após autenticar; falha silenciosa mantém o fallback histórico (E.R.2 ligado, DH desligado). */
async function loadFastchipClusterSettings(): Promise<void> {
  try {
    const settings = await fastchipClusterSettingsService.getSettings();
    setFastchipClusterSettings(settings);
  } catch {
    // Mantém o fallback (E.R.2 ligado, DH desligado) — não bloqueia o app.
  }
}

/** Monta o usuário da sessão a partir do login/refresh. Preserva nome e demais campos quando o refresh só devolve o token. */
function authUserFromLoginResponse(response: LoginResponse, previous: AuthUser | null): AuthUser {
  const nome = (response.nome ?? previous?.nome ?? '').trim();
  const username = (response.username || previous?.username || '').trim();
  const email = (response.email || previous?.email || '').trim();
  const displayName =
    nome || username || (email ? email.split('@')[0] : '') || 'Usuário';

  return {
    id: response.userId || previous?.id || '',
    nome: nome || undefined,
    name: displayName,
    username: username || email,
    email,
    roles: response.roles && response.roles.length > 0 ? response.roles : previous?.roles ?? [],
    isActive: true,
    teamId: response.teamId ?? previous?.teamId,
    podeEditarLinhasContratoBloqueado:
      response.podeEditarLinhasContratoBloqueado ??
      previous?.podeEditarLinhasContratoBloqueado ??
      false,
    rjMgEsFastchipClusterPreference:
      response.rjMgEsFastchipClusterPreference ?? previous?.rjMgEsFastchipClusterPreference ?? null,
  };
}

// Interface do contexto de autenticação
interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
}

// Props do provider
interface AuthProviderProps {
  children: ReactNode;
}

// Criar o contexto
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hook personalizado para usar o contexto
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

// Função para decodificar JWT e extrair expiração
const decodeJWT = (token: string): { exp: number } | null => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};

// Função para verificar se token está próximo de expirar (30 minutos antes)
const isTokenNearExpiration = (token: string): boolean => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return true;

  const expirationTime = decoded.exp * 1000; // Converter para milissegundos
  const currentTime = Date.now();
  const timeUntilExpiration = expirationTime - currentTime;
  const thirtyMinutes = 30 * 60 * 1000; // 30 minutos em milissegundos

  return timeUntilExpiration <= thirtyMinutes;
};

// Função para verificar se token está expirado
const isTokenExpired = (token: string): boolean => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return true;

  const expirationTime = decoded.exp * 1000; // Converter para milissegundos
  const currentTime = Date.now();

  return currentTime >= expirationTime;
};

// Provider do contexto
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { showSuccess, showError } = useToast();

  // Verificar se há token salvo no localStorage ao inicializar
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const savedToken = localStorage.getItem('authToken');
        const savedUser = localStorage.getItem('user');

        if (savedToken && savedUser) {
          // Configurar o token no axios
          apiService.setAuthToken(savedToken);
          setToken(savedToken);
          setUser(JSON.parse(savedUser));

          // Não tentar refresh na inicialização - deixar o interceptor do axios lidar com isso
          // Isso evita o problema de deslog automático ao recarregar a página

          void loadFastchipClusterSettings();
        }
      } catch (error) {
        // Só fazer logout se houver erro crítico
        if (error instanceof SyntaxError) {
          // Erro ao fazer parse do JSON do usuário
          logout();
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Verificação periódica de token e refresh proativo
  useEffect(() => {
    if (!token) return;

    const checkAndRefreshToken = async () => {
      try {
        // Verificar se token está expirado
        if (isTokenExpired(token)) {
          logout();
          return;
        }

        // Verificar se token está próximo de expirar (30 minutos antes)
        if (isTokenNearExpiration(token)) {
          try {
            await refreshToken();
          } catch (error) {
            // Não fazer logout aqui, deixar o interceptor do axios lidar com erro 401
          }
        }
      } catch (error) {
      }
    };

    // Verificar imediatamente
    checkAndRefreshToken();

    // Verificar a cada 30 minutos (1800000 ms)
    const interval = setInterval(checkAndRefreshToken, 30 * 60 * 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Verificar token quando página volta ao foco (após inatividade)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && token) {
        
        try {
          // Verificar se token está expirado
          if (isTokenExpired(token)) {
            logout();
            return;
          }

          // Se está próximo de expirar, fazer refresh
          if (isTokenNearExpiration(token)) {
            try {
              await refreshToken();
            } catch (error) {
            }
          }
        } catch (error) {
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Função de login
  const login = async (credentials: LoginRequest): Promise<void> => {
    try {
      setIsLoading(true);
      const response: LoginResponse = await apiService.login(credentials);

      const userData = authUserFromLoginResponse(response, null);

      // Salvar no estado
      setToken(response.accessToken);
      setUser(userData);

      // Salvar no localStorage
      localStorage.setItem('authToken', response.accessToken);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('tokenTimestamp', Date.now().toString());

      // Configurar interceptor do axios com o novo token
      apiService.setAuthToken(response.accessToken);

      void loadFastchipClusterSettings();

      // Mostrar toast de boas-vindas com nome de exibição (cadastro)
      const welcomeName = userData.name || userData.username || 'Usuário';
      showSuccess(`Bem-vindo, ${welcomeName}!`);

    } catch (error: any) {
      const data = error?.response?.data;
      const isAccountLockout =
        data?.code === 'ACCOUNT_LOCKED' || error?.response?.status === 423;

      // Lockout: mensagem no ecrã de login, sem toast
      if (isAccountLockout) {
        const lockoutError = new Error(
          data?.message || 'Conta temporariamente bloqueada. Tente novamente em alguns minutos.'
        );
        (lockoutError as Error & { isAccountLockout?: boolean }).isAccountLockout = true;
        throw lockoutError;
      }

      if (data?.code === 'DEVICE_NOT_AUTHORIZED') {
        const deviceError = new Error(
          'Computador nao autorizado, procure seu supervisor'
        );
        (deviceError as Error & { isDeviceNotAuthorized?: boolean }).isDeviceNotAuthorized = true;
        throw deviceError;
      }

      const errorMessage = data?.message || 'Erro ao fazer login. Verifique suas credenciais.';
      showError(errorMessage);

      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Função de logout
  const logout = (): void => {
    setUser(null);
    setToken(null);

    // Remover do localStorage
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('tokenTimestamp');

    // Remover token do interceptor
    apiService.removeAuthToken();

    // Mostrar toast de informação
    showSuccess('Logout realizado com sucesso!');

    // Redirecionar para login
    window.location.href = '/login';
  };

  // Função para renovar token
  const refreshToken = async (): Promise<void> => {
    try {
      const response: LoginResponse = await apiService.refreshToken();

      setToken(response.accessToken);
      localStorage.setItem('authToken', response.accessToken);
      localStorage.setItem('tokenTimestamp', Date.now().toString());

      setUser((prev) => {
        const merged = authUserFromLoginResponse(response, prev);
        localStorage.setItem('user', JSON.stringify(merged));
        return merged;
      });

      // Atualizar interceptor
      apiService.setAuthToken(response.accessToken);

    } catch (error: any) {
      showError('Sua sessão expirou. Faça login novamente.');
      logout();
      throw error;
    }
  };

  // Verificar se usuário tem uma role específica
  const hasRole = (role: string): boolean => {
    if (!user || !user.roles) return false;
    return user.roles.includes(role);
  };

  // Verificar se usuário tem qualquer uma das roles
  const hasAnyRole = (roles: string[]): boolean => {
    if (!user || !user.roles) return false;
    return user.roles.some(userRole => roles.includes(userRole));
  };

  // Valor do contexto
  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    isLoading,
    login,
    logout,
    refreshToken,
    hasRole,
    hasAnyRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
